import { useTranslation } from "react-i18next";
import { useLangFont } from "../../../hooks/useLangFont";

const benefitKeys = [
  { icon: "ri-calendar-check-line", titleKey: "b2b_benefit_0_title", descKey: "b2b_benefit_0_desc" },
  { icon: "ri-flashlight-line", titleKey: "b2b_benefit_1_title", descKey: "b2b_benefit_1_desc" },
  { icon: "ri-user-star-line", titleKey: "b2b_benefit_2_title", descKey: "b2b_benefit_2_desc" },
  { icon: "ri-file-chart-line", titleKey: "b2b_benefit_3_title", descKey: "b2b_benefit_3_desc" },
];

const statKeys = [
  { valueKey: "b2b_stat_0_val", labelKey: "b2b_stat_0_label", subKey: "b2b_stat_0_sub" },
  { valueKey: "b2b_stat_1_val", labelKey: "b2b_stat_1_label", subKey: "b2b_stat_1_sub" },
  { valueKey: "b2b_stat_2_val", labelKey: "b2b_stat_2_label", subKey: "b2b_stat_2_sub" },
  { valueKey: "b2b_stat_3_val", labelKey: "b2b_stat_3_label", subKey: "b2b_stat_3_sub" },
];

const targetKeys = [
  "b2b_target_0", "b2b_target_1", "b2b_target_2", "b2b_target_3", "b2b_target_4",
];

export default function B2BPartnership() {
  const { t } = useTranslation();
  const { heading, body } = useLangFont();

  return (
    <section id="b2b" className="py-28 bg-[#1A2332] w-full overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">

        {/* Header */}
        <div className="text-center mb-16">
          <span
            className="inline-block bg-[#C9A84C] text-[#1A2332] text-xs font-bold tracking-[0.3em] uppercase px-4 py-2 rounded-full mb-5"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            {t("b2b_badge")}
          </span>
          <h2
            className="text-4xl md:text-5xl font-bold text-white leading-tight mb-4 tracking-tight"
            style={{ fontFamily: heading }}
          >
            {t("b2b_title")}
            <span className="text-[#C9A84C]">{t("b2b_highlight")}</span>
          </h2>
          <p
            className="text-white/55 text-sm max-w-xl mx-auto leading-relaxed"
            style={{ fontFamily: body }}
          >
            {t("b2b_desc")}
          </p>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border border-white/10 rounded-2xl overflow-hidden mb-14">
          {statKeys.map((s, i) => (
            <div
              key={s.labelKey}
              className={`flex flex-col items-center justify-center py-8 px-4 ${i < statKeys.length - 1 ? "border-r border-white/10" : ""}`}
              style={{ background: "rgba(255,255,255,0.04)" }}
            >
              <span
                className="text-4xl font-bold text-[#C9A84C] leading-none mb-1"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                {t(s.valueKey)}
              </span>
              <span
                className="text-white text-sm font-semibold tracking-wide mt-1"
                style={{ fontFamily: body }}
              >
                {t(s.labelKey)}
              </span>
              <span
                className="text-white/35 text-[10px] mt-0.5"
                style={{ fontFamily: body }}
              >
                {t(s.subKey)}
              </span>
            </div>
          ))}
        </div>

        {/* Benefits + CTA */}
        <div className="flex flex-col xl:flex-row gap-10 items-start">
          <div className="w-full xl:w-2/3 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {benefitKeys.map((b) => (
              <div
                key={b.titleKey}
                className="flex flex-col gap-3 bg-white/5 hover:bg-white/8 transition-colors duration-300 rounded-2xl p-6 cursor-default border border-white/8"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#C9A84C]/20 text-[#C9A84C]">
                  <i className={`${b.icon} text-xl`} />
                </div>
                <span
                  className="text-white font-semibold text-sm tracking-wide"
                  style={{ fontFamily: body }}
                >
                  {t(b.titleKey)}
                </span>
                <span
                  className="text-white/50 text-xs leading-relaxed"
                  style={{ fontFamily: body }}
                >
                  {t(b.descKey)}
                </span>
              </div>
            ))}
          </div>

          {/* Right: CTA card */}
          <div className="w-full xl:w-1/3 rounded-2xl border border-[#C9A84C]/30 p-8 flex flex-col gap-6"
            style={{ background: "rgba(201,168,76,0.06)" }}
          >
            <div>
              <p
                className="text-[#C9A84C] text-xs font-bold tracking-[0.3em] uppercase mb-3"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                {t("b2b_target_label")}
              </p>
              <ul className="flex flex-col gap-3">
                {targetKeys.map((key) => (
                  <li key={key} className="flex items-center gap-3">
                    <i className="ri-checkbox-circle-fill text-[#C9A84C] text-base flex-shrink-0 w-4 h-4 flex items-center justify-center" />
                    <span
                      className="text-white/75 text-sm"
                      style={{ fontFamily: body }}
                    >
                      {t(key)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="border-t border-white/10 pt-6">
              <p
                className="text-white/45 text-xs mb-4 leading-relaxed"
                style={{ fontFamily: body }}
              >
                {t("b2b_cta_note")}
              </p>
              <a
                href="#contact"
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
                }}
                className="btn-lift w-full flex items-center justify-center gap-3 bg-[#C9A84C] hover:bg-[#B8973B] text-[#1A2332] font-bold text-sm px-8 py-4 rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer"
                style={{ fontFamily: body }}
              >
                {t("b2b_cta_btn")}
                <i className="ri-arrow-right-line text-base" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}